import { convertJaqlDataSource, DEFAULT_PIVOT_GRAND_TOTALS, isPivotAttribute, isPivotMeasure, } from '@sisense/sdk-data';
import merge from 'ts-deepmerge';
import { v4 as uuid } from 'uuid';
import { applyHighlightFilters, matchHighlightsWithAttributes } from './metadata/highlights.js';
import { getPivotMetadataStats, normalizeLastRowSorting, preparePivotRowJaqlSortOptions, } from './pivot/pivot-query-utils.js';
const JAQL_BY_CSDK = 'ComposeSDK';
/**
 * @internal
 */
export function getJaqlQueryPayload(queryDescription, shouldSkipHighlightsWithoutAttributes) {
    const { attributes, measures, filters, filterRelations, highlights, dataSource, count, offset, ungroup, } = queryDescription;
    // "ungroup: true" will be set in JAQL when two conditions are met:
    // (1) ungroup is set to true
    // AND
    // (2) the query has no measures (unaggregated query)
    const includeUngroup = ungroup && measures.length === 0;
    const queryPayload = Object.assign({ metadata: prepareQueryMetadata({ attributes, measures, filters, filterRelations, highlights }, shouldSkipHighlightsWithoutAttributes) }, prepareQueryOptions(dataSource, count, offset, includeUngroup));
    if (filterRelations) {
        return Object.assign(Object.assign({}, queryPayload), { filterRelations });
    }
    return queryPayload;
}
function prepareFilterMetadata(attributesMetadata, filters, filterRelations, highlights, shouldSkipHighlightsWithoutAttributes) {
    const [highlightsWithAttributes, highlightsWithoutAttributes] = matchHighlightsWithAttributes(attributesMetadata, highlights);
    attributesMetadata.forEach((d) => applyHighlightFilters(d, highlightsWithAttributes));
    const getFilterJaql = (f) => {
        const jaql = f.jaql();
        if (filterRelations) {
            // instanceid is needed for filter relations only
            if (Array.isArray(jaql)) {
                return jaql.map((j) => (Object.assign(Object.assign({}, j), { instanceid: f.config.guid })));
            }
            else {
                return Object.assign(Object.assign({}, jaql), { instanceid: f.config.guid });
            }
        }
        return jaql;
    };
    const filtersMetadata = (shouldSkipHighlightsWithoutAttributes
        ? filters.flatMap((d) => getFilterJaql(d))
        : [...filters, ...highlightsWithoutAttributes].flatMap((d) => getFilterJaql(d))).filter((f) => {
        return Object.keys(f.jaql.filter || {}).length !== 0;
    });
    if (shouldSkipHighlightsWithoutAttributes && highlightsWithoutAttributes.length > 0) {
        console.warn(`The following highlight filters were not applied due to missing attributes: [${highlightsWithoutAttributes
            .map((f) => f.attribute.name)
            .join(', ')}].`);
    }
    return filtersMetadata;
}
/**
 * WORKAROUND
 *
 * Ensures that any `text`-typed context fields with `agg: "count"`
 * in analytic functions like `trend` or `forecast` are converted to `numeric`.
 *
 * This helps maintain compatibility with analytic functions that expect
 * numeric input for aggregation.
 *
 * @param {MetadataItem} metadataItem - The metadata item to check and adjust.
 * @returns {MetadataItem} A cloned and modified metadata item if needed, or the original one.
 *
 * @internal
 */
function ensureNumericCountInContext(metadataItem) {
    var _a, _b;
    const formula = (_a = metadataItem.jaql) === null || _a === void 0 ? void 0 : _a.formula;
    if (!formula || (!formula.includes('trend') && !formula.includes('forecast'))) {
        return metadataItem;
    }
    const clonedItem = structuredClone(metadataItem);
    const context = (_b = clonedItem.jaql) === null || _b === void 0 ? void 0 : _b.context;
    if (context) {
        for (const key in context) {
            const item = context[key];
            if (item.datatype === 'text' && item.agg === 'count') {
                item.datatype = 'numeric';
            }
        }
    }
    return clonedItem;
}
function prepareQueryMetadata(metadataDescription, shouldSkipHighlightsWithoutAttributes) {
    const { attributes, measures, filters, filterRelations, highlights } = metadataDescription;
    const attributesMetadata = attributes.map((d) => d.jaql());
    const measuresMetadata = measures.map((d) => ensureNumericCountInContext(d.jaql()));
    const filtersMetadata = prepareFilterMetadata(attributesMetadata, filters, filterRelations, highlights, shouldSkipHighlightsWithoutAttributes);
    return [...attributesMetadata, ...measuresMetadata, ...filtersMetadata];
}
export function prepareQueryOptions(dataSource, count, offset, includeUngroup) {
    return Object.assign(Object.assign(Object.assign({ datasource: convertJaqlDataSource(dataSource), by: JAQL_BY_CSDK, queryGuid: uuid() }, (includeUngroup ? { ungroup: true } : {})), (count ? { count } : {})), (offset ? { offset } : {}));
}
/**
 * Converts a pivot query description to a JAQL query payload.
 *
 * @param pivotQueryDescription
 * @param shouldSkipHighlightsWithoutAttributes
 * @returns jaql query payload
 */
export function getPivotJaqlQueryPayload(pivotQueryDescription, shouldSkipHighlightsWithoutAttributes) {
    const { rowsAttributes, columnsAttributes, measures, filters, filterRelations, grandTotals, highlights, dataSource, count, offset, } = pivotQueryDescription;
    const queryPayload = Object.assign({ metadata: preparePivotQueryMetadata({ rowsAttributes, columnsAttributes, measures, filters, highlights, filterRelations }, shouldSkipHighlightsWithoutAttributes) }, preparePivotQueryOptions(dataSource, grandTotals, count, offset));
    if (filterRelations) {
        return Object.assign(Object.assign({}, queryPayload), { filterRelations });
    }
    return queryPayload;
}
function jaqlPivotAttribute(a, panel, index, metadataStats) {
    const isSortedRowAttribute = panel === 'rows' && isPivotAttribute(a) && a.sort && a.sort.direction !== 'sortNone';
    const jaql = Object.assign(Object.assign({}, (isPivotAttribute(a) ? a.attribute.jaql(true) : a.jaql(true))), (isSortedRowAttribute && preparePivotRowJaqlSortOptions(a.sort, index, metadataStats)));
    return Object.assign(Object.assign({ jaql }, (isPivotAttribute(a) && a.includeSubTotals ? { format: { subtotal: true } } : {})), { panel, field: { index: index, id: `${panel}-${index}` } });
}
function jaqlPivotMeasure(m, panel, index) {
    return Object.assign(Object.assign({}, (isPivotMeasure(m)
        ? merge(m.measure.jaql(), {
            jaql: { subtotalAgg: m.totalsCalculation },
            format: Object.assign({ databars: m.dataBars || false }, (m.shouldRequestMinMax && {
                color: { type: 'range' },
            })),
        })
        : m.jaql())), { panel, field: { index: index, id: `${panel}-${index}` } });
}
/**
 * Prepares the metadata part of the JAQL payload for a pivot query.
 *
 * @param pivotMetadataDescription
 * @param shouldSkipHighlightsWithoutAttributes
 * @returns array of metadata items of the JAQL payload
 */
function preparePivotQueryMetadata(pivotMetadataDescription, shouldSkipHighlightsWithoutAttributes) {
    const { rowsAttributes, columnsAttributes, measures, filters, filterRelations, highlights } = pivotMetadataDescription;
    const metadataStats = getPivotMetadataStats(rowsAttributes, columnsAttributes, measures);
    let fieldIndex = 0; // used as a global counter to build field.index in Jaql MetadataItem
    const rowsAttributesMetadata = rowsAttributes.map((a, index) => jaqlPivotAttribute(a, 'rows', index + fieldIndex, metadataStats));
    fieldIndex = fieldIndex + rowsAttributes.length;
    const columnsAttributesMetadata = columnsAttributes.map((a, index) => jaqlPivotAttribute(a, 'columns', index + fieldIndex, metadataStats));
    fieldIndex = fieldIndex + columnsAttributes.length;
    const attributesMetadata = [
        ...rowsAttributesMetadata,
        ...columnsAttributesMetadata,
    ];
    const measuresMetadata = measures.map((m, index) => jaqlPivotMeasure(m, 'measures', index + fieldIndex));
    const filtersMetadata = prepareFilterMetadata(attributesMetadata, filters, filterRelations, highlights, shouldSkipHighlightsWithoutAttributes);
    const metadata = [...attributesMetadata, ...measuresMetadata, ...filtersMetadata];
    normalizeLastRowSorting(metadata, metadataStats);
    return metadata;
}
/**
 * Prepares the options part of the JAQL payload for a pivot query.
 *
 * @param dataSource
 * @param grandTotals
 * @param count
 * @param offset
 */
function preparePivotQueryOptions(dataSource, grandTotals, count, offset) {
    return Object.assign(Object.assign(Object.assign({ datasource: convertJaqlDataSource(dataSource), by: JAQL_BY_CSDK, queryGuid: uuid(), dashboard: JAQL_BY_CSDK, widget: JAQL_BY_CSDK, format: 'pivot' }, (count ? { count } : {})), (offset ? { offset } : {})), { grandTotals: Object.assign(Object.assign({}, DEFAULT_PIVOT_GRAND_TOTALS), grandTotals) });
}
//# sourceMappingURL=get-jaql-query-payload.js.map